import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Server, Shield, Database } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ArchitectureSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Cloud,
      key: 'cloud',
      gradient: 'from-[#00F0FF]/20 to-transparent'
    },
    {
      icon: Server,
      key: 'microservices',
      gradient: 'from-[#00F0FF]/20 to-transparent'
    },
    {
      icon: Shield,
      key: 'security',
      gradient: 'from-[#00F0FF]/20 to-transparent'
    },
    {
      icon: Database,
      key: 'distributed',
      gradient: 'from-[#00F0FF]/20 to-transparent'
    }
  ];

  return (
    <section 
      id="architecture" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="architecture-section"
    >
      {/* Background Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 240, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <motion.span 
            className="text-[#00F0FF] font-mono text-sm tracking-[0.3em] uppercase"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('architecture.subtitle')}
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 tracking-tight">
            {t('architecture.title')}
          </h2>
          <p className="text-[#A3A3A3] text-lg mt-6 max-w-2xl leading-relaxed">
            {t('architecture.description')}
          </p>
        </motion.div>

        {/* Features Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="architecture-grid">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ scale: 1.02, borderColor: 'rgba(0, 240, 255, 0.5)' }}
              className="group relative p-8 bg-[#050505] border border-white/10 rounded-sm overflow-hidden transition-all duration-300"
            >
              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              {/* Icon */}
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.8 }}
                className="relative z-10 w-14 h-14 flex items-center justify-center border border-[#00F0FF]/30 rounded-sm mb-6 bg-black/50"
              >
                <feature.icon className="w-7 h-7 text-[#00F0FF]" />
              </motion.div>

              {/* Content */}
              <h3 className="relative z-10 text-xl font-semibold text-white mb-3">
                {t(`architecture.features.${feature.key}.title`)}
              </h3>
              <p className="relative z-10 text-[#A3A3A3] leading-relaxed">
                {t(`architecture.features.${feature.key}.description`)}
              </p>

              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-[#00F0FF]/10 to-transparent" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-16 p-8 bg-[#050505] border border-white/10 rounded-sm"
        >
          <div className="flex flex-wrap items-center justify-center gap-8 text-[#A3A3A3] font-mono text-sm">
            {['Kubernetes', 'Docker', 'AWS', 'PostgreSQL', 'Redis', 'Kafka', 'GraphQL', 'gRPC'].map((tech, index) => (
              <motion.span
                key={tech}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ color: '#00F0FF', scale: 1.1 }}
                className="cursor-default transition-colors duration-300"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArchitectureSection;
